import { syncBootSplashTimeline } from '@/lib/boot-splash-timeline';
import type { AppUiHtml } from './types';

/** A delayed flag response must never reload over a new draft or open modal. */
export function createAppUiReloadGuard(doc = document) {
  let revision = 0;
  const changed = () => {
    revision += 1;
  };
  const events = ['input', 'change', 'pointerdown', 'keydown', 'submit'];
  for (const event of events) doc.addEventListener(event, changed, true);
  return {
    activityRevision: () => revision,
    isBlocked: () =>
      Array.from(
        doc.querySelectorAll(
          '[role="dialog"], [role="alertdialog"], dialog[open]'
        )
      ).some(
        (node) =>
          node.getAttribute('aria-hidden') !== 'true' &&
          node.getClientRects().length > 0
      ),
    dispose: () => {
      for (const event of events) doc.removeEventListener(event, changed, true);
    },
  };
}

/** Replace only the selected UI's fonts, metadata and pre-JS splash. */
export function applyAppUiHtml(html: AppUiHtml, doc = document): void {
  for (const node of doc.head.querySelectorAll('[data-app-ui-shell]')) {
    node.remove();
  }
  const template = doc.createElement('template');
  template.innerHTML = html.head;
  for (const script of template.content.querySelectorAll('script')) {
    script.remove();
  }
  for (const child of template.content.children) {
    child.setAttribute('data-app-ui-shell', '');
  }
  doc.head.append(template.content);

  const body = new DOMParser().parseFromString(
    `<html><body ${html.bodyAttributes}></body></html>`,
    'text/html'
  ).body;
  for (const attribute of Array.from(doc.body.attributes)) {
    doc.body.removeAttribute(attribute.name);
  }
  for (const attribute of Array.from(body.attributes)) {
    doc.body.setAttribute(attribute.name, attribute.value);
  }
  const root = doc.getElementById('root');
  if (root) {
    root.innerHTML = html.rootHtml;
    // Source HTML's timing script belongs to the pre-JS document; never run
    // it a second time while selecting a bundle.
    for (const script of root.querySelectorAll('script')) script.remove();
    syncBootSplashTimeline(root);
  }
}

const escapeAttribute = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Snapshot the document's own shell (main's, as served) in the shape
 * `applyAppUiHtml` takes, so a failed V2 start can put it back before main.
 */
export function captureAppUiHtml(doc = document): AppUiHtml {
  return {
    head: Array.from(doc.head.querySelectorAll('[data-app-ui-shell]'))
      .map((node) => node.outerHTML)
      .join(''),
    bodyAttributes: Array.from(doc.body.attributes)
      .map(({ name, value }) => `${name}="${escapeAttribute(value)}"`)
      .join(' '),
    rootHtml: doc.getElementById('root')?.innerHTML ?? '',
  };
}

export async function loadAppUiStyles(
  styles: string[],
  doc = document
): Promise<void> {
  await Promise.all(
    styles.map(
      (href) =>
        new Promise<void>((resolve, reject) => {
          const link = doc.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.dataset.appUiStyles = 'main';
          link.onload = () => resolve();
          link.onerror = () => reject(new Error('Could not load app styles'));
          doc.head.append(link);
        })
    )
  );
}
